import React from "react";
import { motion } from "framer-motion";

const ROWS = [
  { feature: "Investment Readiness Score", vawena: true, zillow: false, loopnet: false },
  { feature: "Portfolio Performance Tracking", vawena: true, zillow: false, loopnet: false },
  { feature: "DSCR Analysis & Lender Reports", vawena: true, zillow: false, loopnet: false },
  { feature: "Net Worth & Buying Power", vawena: true, zillow: false, loopnet: false },
  { feature: "AI Financial Insights", vawena: true, zillow: false, loopnet: false },
  { feature: "Schedule E Tax Reports", vawena: true, zillow: false, loopnet: false },
  { feature: "Underperformance Alerts", vawena: true, zillow: false, loopnet: false },
  { feature: "Property Search / Listings", vawena: false, zillow: true, loopnet: true },
];

const Check = () => (
  <div className="flex justify-center">
    <div className="w-7 h-7 rounded-full bg-blue-500/15 flex items-center justify-center">
      <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 14 14" stroke="currentColor" strokeWidth="2.2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 7l3.5 3.5 5.5-7" />
      </svg>
    </div>
  </div>
);

const Cross = () => (
  <div className="flex justify-center">
    <svg className="w-4 h-4 dark:text-white/15 text-gray-300" fill="none" viewBox="0 0 14 14" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 11l8-8M11 11L3 3" />
    </svg>
  </div>
);

export const ComparisonSection: React.FC = () => {
  return (
    <section className="relative w-full px-4 sm:px-6 py-20 overflow-hidden">
      <div className="absolute inset-0 dark:bg-vw-surface/20 bg-blue-50/30 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent dark:via-blue-500/20 via-blue-300/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent dark:via-blue-500/20 via-blue-300/40 to-transparent" />

      <div className="max-w-[860px] mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-5">
            Why VaWeNa is different
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-4 leading-tight">
            Zillow and LoopNet help you{" "}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">find</span>{" "}
            properties.
          </h2>
          <p className="text-base dark:text-slate-400 text-gray-500 max-w-lg mx-auto leading-relaxed">
            VaWeNa tells you if you're actually <strong className="dark:text-white text-gray-900">ready to buy</strong> — and whether your current investments are <strong className="dark:text-white text-gray-900">working as hard as you are.</strong>
          </p>
        </div>

        {/* Comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="dark:bg-white/[0.02] bg-white border dark:border-white/[0.08] border-gray-200 rounded-2xl overflow-hidden shadow-sm"
        >
          {/* Header row */}
          <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] sm:grid-cols-4 border-b dark:border-white/[0.06] border-gray-100">
            <div className="p-4 sm:p-5" />
            {[
              { name: "VaWeNa", highlight: true },
              { name: "Zillow", highlight: false },
              { name: "LoopNet", highlight: false },
            ].map(({ name, highlight }) => (
              <div
                key={name}
                className={`px-2 sm:px-5 p-4 sm:p-5 text-center border-l dark:border-white/[0.06] border-gray-100 ${
                  highlight ? "dark:bg-blue-500/10 bg-blue-50" : ""
                }`}
              >
                <p className={`text-xs sm:text-sm font-bold ${highlight ? "dark:text-blue-400 text-blue-600" : "dark:text-slate-400 text-gray-400"}`}>
                  {name}
                </p>
              </div>
            ))}
          </div>

          {/* Feature rows */}
          {ROWS.map(({ feature, vawena, zillow, loopnet }, i) => (
            <div
              key={feature}
              className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] sm:grid-cols-4 border-b dark:border-white/[0.04] border-gray-50 last:border-b-0 ${
                i % 2 === 1 ? "dark:bg-white/[0.01] bg-gray-50/40" : ""
              }`}
            >
              <div className="px-3 sm:px-5 py-3.5 text-xs sm:text-sm dark:text-slate-400 text-gray-600 flex items-center">
                {feature}
              </div>
              <div className="px-2 sm:px-5 py-3.5 border-l dark:border-white/[0.06] border-gray-100 dark:bg-blue-500/[0.03] bg-blue-50/30 flex items-center justify-center">
                {vawena ? <Check /> : <Cross />}
              </div>
              <div className="px-2 sm:px-5 py-3.5 border-l dark:border-white/[0.06] border-gray-100 flex items-center justify-center">
                {zillow ? <Check /> : <Cross />}
              </div>
              <div className="px-2 sm:px-5 py-3.5 border-l dark:border-white/[0.06] border-gray-100 flex items-center justify-center">
                {loopnet ? <Check /> : <Cross />}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Callout */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center text-sm dark:text-slate-400 text-gray-500"
        >
          Use Zillow to find your next deal.{" "}
          <span className="dark:text-white text-gray-900 font-semibold">Use VaWeNa to know if you should buy it.</span>
        </motion.p>
      </div>
    </section>
  );
};
